import { convertObjectToCamelCase } from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type {
  SupplyTransactionCreatePayload,
  SupplyTransactionProps,
  SupplyTransactionWithPagesProps,
} from '@/types'

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  BFF_SUPPLY_TRANSACTION_URL,
  BFF_SUPPLY_TRANSACTIONS_URL,
} from './api.urls'

export const useGenerateSupplierTransaction = () => {
  const generateSupplierTransaction = async (
    payload: SupplyTransactionCreatePayload,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyTransactionProps
  }> => {
    try {
      const { data } = await api.post(BFF_SUPPLY_TRANSACTIONS_URL, payload)

      return {
        success: true,
        message: 'successfully!',
        data,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLY_TRANSACTION.module],
    mutationFn: (payload: SupplyTransactionCreatePayload) =>
      generateSupplierTransaction(payload),
  })
}

export const useSupplierTransactionList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getSupplierTransactionList = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyTransactionWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_SUPPLY_TRANSACTIONS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Supplier Order fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as SupplyTransactionWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supplier order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLY_TRANSACTION.module, params],
    queryFn: () => getSupplierTransactionList(),
    enabled: isEnabled,
  })
}

export const useSupplierTransactionDetail = (uuid?: string) => {
  const getSupplierTransactionDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyTransactionProps
  }> => {
    try {
      const { data } = await api.get(BFF_SUPPLY_TRANSACTION_URL(`${uuid}`))

      return {
        success: true,
        message: 'Supplier Order fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplyTransactionProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Supplier Order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLY_TRANSACTION.module, uuid],
    queryFn: () => getSupplierTransactionDetail(),
    enabled: !!uuid,
  })
}

export const useSupplierTransactionEdit = (uuid: string) => {
  const supplierTransactionEdit = async (
    payload: Partial<SupplyTransactionProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyTransactionProps
  }> => {
    try {
      const { data } = await api.patch(
        BFF_SUPPLY_TRANSACTION_URL(`${uuid}`),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyTransactionProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLY_TRANSACTION.module],
    mutationFn: (payload: Partial<SupplyTransactionProps>) =>
      supplierTransactionEdit(payload),
  })
}
